import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';

export const SmartConnectivityPage = () => {
  return (
    <Layout>
      {/* Back Button */}
      <div className="container-xl pt-24 pb-2">
        <Link to="/motorization" className="inline-flex items-center text-neutral-500 hover:text-neutral-900 transition-colors">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Motorization
        </Link>
      </div>

      {/* Hero Section - Title overlaid on banner image */}
      <section className="relative h-[45vh] lg:h-[50vh] overflow-hidden" data-testid="smart-connectivity-hero">
        <img 
          src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=2000&q=85"
          alt="Smart home living room with motorized blinds"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/10" />
        <div className="container-xl relative z-10 h-full flex flex-col justify-end pb-16 lg:pb-24">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="max-w-4xl"
          >
            <h1 className="text-white text-5xl lg:text-6xl font-serif leading-tight mb-4">
              Smart Home Connectivity
            </h1>
            <p className="text-2xl lg:text-3xl text-white/80 font-light">
              Homes. Buildings. Anywhere.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 1: Smart Platforms - Contained layout with rounded corners */}
      <section className="py-16 lg:py-24 bg-white" data-testid="smart-platforms-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg">
            {/* Image LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/hvf9eyrb_image.png"
                alt="Smart home platforms - voice assistants and smart devices"
                className="w-full h-full object-cover"
              />
            </motion.div>
            
            {/* Text RIGHT with gray background */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#f5f5f5] flex flex-col justify-center px-10 lg:px-16 py-12 lg:py-16"
            >
              <h2 className="text-3xl lg:text-4xl font-serif mb-5">Smart platforms</h2>
              <p className="text-neutral-600 text-base leading-relaxed mb-8">
                Motionblinds motors work with all major smart home platforms including Google Home, 
                Apple Home, Amazon Alexa, Samsung SmartThings, and building management systems 
                including KNX, Control4, Loxone and others.
              </p>
              <div>
                <Link to="/motorization/smart-connectivity/works-with-motionblinds">
                  <Button 
                    variant="outline" 
                    className="border-neutral-300 text-neutral-900 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 rounded-full px-8 py-5 text-sm font-medium bg-transparent"
                  >
                    View all
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: Motor Technology - Text LEFT, Image RIGHT */}
      <section className="pb-16 lg:pb-24 bg-white" data-testid="motor-technology-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg">
            {/* Text LEFT with gray background */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#f5f5f5] flex flex-col justify-center px-10 lg:px-16 py-12 lg:py-16 order-2 lg:order-1"
            >
              <h2 className="text-3xl lg:text-4xl font-serif mb-5">Motor technology</h2>
              <p className="text-neutral-600 text-base leading-relaxed mb-8">
                Find out about the built-in technologies in our smart battery and wired motors 
                including Matter, Thread, Bluetooth and 433MHz. Explore our complete range with 
                numerous connectivity options.
              </p>
              <div>
                <Link to="/motorization/smart-connectivity/built-in-motor-technology">
                  <Button 
                    variant="outline" 
                    className="border-neutral-300 text-neutral-900 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 rounded-full px-8 py-5 text-sm font-medium bg-transparent"
                  >
                    Discover more
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            {/* Image RIGHT */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/7njuizwa_image.png"
                alt="Motor technology - smart motor with circuit board"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Unfolding - News Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="news-section">
        <div className="container-xl">
          <div className="mb-12">
            <h2 className="text-3xl lg:text-4xl font-serif mb-2">What's unfolding</h2>
            <p className="text-neutral-400 text-lg">Fresh news. Emerging innovations.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* News Card 1 - Matter */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group"
            >
              <Link to="/motorization/smart-connectivity/matter" className="block">
                <div className="aspect-[4/3] overflow-hidden mb-5">
                  <img 
                    src="/img/catalog/smart-home-secondary.jpg" 
                    alt="Matter smart home standard"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-amber-700 transition-colors">
                  The future of smart has arrived
                </h3>
                <p className="text-neutral-500 mb-3 text-sm leading-relaxed">
                  Matter is here – our Eve Motionblinds motors are ready to be used with the new 
                  standard for smart home devices.
                </p>
                <span className="text-amber-700 font-medium text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Read more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>

            {/* News Card 2 - Home Assistant */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group"
            >
              <Link to="/motorization/smart-connectivity/home-assistant" className="block">
                <div className="aspect-[4/3] overflow-hidden mb-5 bg-neutral-900 flex items-center justify-center">
                  <div className="text-center p-6">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <span className="text-white text-lg font-bold">Motionblinds</span>
                      <span className="text-neutral-500 text-lg">×</span>
                      <span className="text-cyan-400 text-lg font-bold">Home Assistant</span>
                    </div>
                    <p className="text-neutral-400 text-xs">Official Partnership</p>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-amber-700 transition-colors">
                  First official shading partner of Home Assistant
                </h3>
                <p className="text-neutral-500 mb-3 text-sm leading-relaxed">
                  Working with the world's leading open-source smart home platform.
                </p>
                <span className="text-amber-700 font-medium text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Read more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>

            {/* News Card 3 - Sustainable Buildings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group"
            >
              <Link to="/blog" className="block">
                <div className="aspect-[4/3] overflow-hidden mb-5">
                  <img 
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80" 
                    alt="Sustainable smart buildings"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-amber-700 transition-colors">
                  Smart shading for sustainable buildings
                </h3>
                <p className="text-neutral-500 mb-3 text-sm leading-relaxed">
                  How Motionblinds wired motors support smarter, greener, and more comfortable spaces.
                </p>
                <span className="text-amber-700 font-medium text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Read more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#f5f5f5]" data-testid="cta-section">
        <div className="container-xl">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Find Store */}
            <div className="bg-white p-8">
              <h3 className="text-xl font-semibold mb-2">Where to buy Motionblinds?</h3>
              <p className="text-neutral-500 mb-6 text-sm">Find a retailer or dealer near you.</p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-full px-6 text-sm">
                    Find your store
                  </Button>
                </Link>
                <Link to="/trade">
                  <Button variant="outline" className="border-neutral-300 text-neutral-900 hover:bg-neutral-900 hover:text-white rounded-full px-6 text-sm">
                    For professionals
                  </Button>
                </Link>
              </div>
            </div>

            {/* Support */}
            <div className="bg-white p-8">
              <h3 className="text-xl font-semibold mb-2">Support center | FAQ</h3>
              <p className="text-neutral-500 mb-6 text-sm">Get answers to common questions.</p>
              <Link to="/faq">
                <Button className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-full px-6 text-sm">
                  Find your answer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SmartConnectivityPage;
